"use client";

/**
 * SwishCheckoutDialog
 * Shows a QR code (desktop) or deep-link button (mobile) so a Swedish user can
 * pay via Swish. Polls /api/swish/status/:id every 2 s and calls onSuccess once
 * the payment is confirmed.
 *
 * Usage:
 *   <SwishCheckoutDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     amountSEK={149}
 *     message="Download: My Song"
 *     itemId={workId}
 *     itemType="download"
 *     itemTitle="My Song"
 *     buyerId={user?.uid}
 *     buyerEmail={user?.email ?? undefined}
 *     onSuccess={(instructionId) => handleSwishPaid(instructionId)}
 *   />
 */

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, Smartphone, QrCode } from "lucide-react";
import { cn } from "@/lib/utils";

export type SwishItemType = 'download' | 'marketplace' | 'event' | 'sync';

interface SwishCheckoutDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  amountSEK: number;
  message: string;
  itemId?: string;
  itemType?: SwishItemType;
  itemTitle?: string;
  buyerId?: string;
  buyerEmail?: string;
  onSuccess: (instructionId: string) => void;
  onError?: (msg: string) => void;
}

type Phase = 'idle' | 'creating' | 'waiting' | 'paid' | 'failed' | 'not_configured';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS  = 5 * 60 * 1000; // 5 minutes max

export function SwishCheckoutDialog({
  open,
  onOpenChange,
  amountSEK,
  message,
  itemId,
  itemType,
  itemTitle,
  buyerId,
  buyerEmail,
  onSuccess,
  onError,
}: SwishCheckoutDialogProps) {
  const [phase, setPhase]           = useState<Phase>('idle');
  const [instructionId, setId]      = useState<string | null>(null);
  const [deepLink, setDeepLink]     = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl]   = useState<string | null>(null);
  const [errorMsg, setErrorMsg]     = useState<string | null>(null);
  const [isMobile, setIsMobile]     = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef   = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect mobile
  useEffect(() => {
    setIsMobile(/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));
  }, []);

  // ── Start payment when dialog opens ──────────────────────────────────────
  const startPayment = useCallback(async () => {
    setPhase('creating');
    setErrorMsg(null);
    setId(null);
    setDeepLink(null);
    setQrCodeUrl(null);

    try {
      const res = await fetch('/api/swish/create-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amountSEK,
          message,
          itemId,
          itemType,
          itemTitle,
          buyerId,
          buyerEmail,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'swish_not_configured') {
          setPhase('not_configured');
          return;
        }
        throw new Error(data.error || data.message || 'Could not create Swish payment');
      }

      setId(data.instructionId);
      setDeepLink(data.deepLink ?? null);
      setQrCodeUrl(data.qrCodeUrl ?? null);
      setPhase('waiting');

      // Auto-open Swish on mobile
      if (isMobile && data.deepLink) {
        window.location.href = data.deepLink;
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to initiate Swish payment');
      setPhase('failed');
      onError?.(err.message);
    }
  }, [amountSEK, message, itemId, itemType, itemTitle, buyerId, buyerEmail, isMobile, onError]);

  useEffect(() => {
    if (open) startPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Poll status ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'waiting' || !instructionId) return;

    const poll = async () => {
      try {
        const res  = await fetch(`/api/swish/status/${instructionId}`);
        const data = await res.json();

        if (data.status === 'PAID') {
          clearInterval(pollTimerRef.current!);
          clearTimeout(timeoutRef.current!);
          setPhase('paid');
          setTimeout(() => onSuccess(instructionId), 1200);
        } else if (
          data.status === 'DECLINED' ||
          data.status === 'ERROR'    ||
          data.status === 'CANCELLED'
        ) {
          clearInterval(pollTimerRef.current!);
          clearTimeout(timeoutRef.current!);
          setPhase('failed');
          const msg = data.errorMessage || `Payment ${data.status.toLowerCase()}`;
          setErrorMsg(msg);
          onError?.(msg);
        }
      } catch { /* ignore transient network errors */ }
    };

    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);

    // Timeout
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollTimerRef.current!);
      setPhase('failed');
      setErrorMsg('Payment timed out. Please try again.');
      onError?.('Payment timed out');
    }, POLL_TIMEOUT_MS);

    return () => {
      clearInterval(pollTimerRef.current!);
      clearTimeout(timeoutRef.current!);
    };
  }, [phase, instructionId, onSuccess, onError]);

  // ── Cleanup on close ─────────────────────────────────────────────────────
  const handleClose = () => {
    clearInterval(pollTimerRef.current!);
    clearTimeout(timeoutRef.current!);
    setPhase('idle');
    onOpenChange(false);
  };

  const formattedAmount = amountSEK.toLocaleString('sv-SE', {
    style: 'currency', currency: 'SEK', minimumFractionDigits: 0,
  });

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-sm text-center">
        <DialogHeader className="items-center">
          {/* Swish logo-like color mark */}
          <div className="w-14 h-14 rounded-2xl bg-[#E9F8F2] flex items-center justify-center mb-1">
            <span className="text-[#00B67A] font-black text-xl tracking-tight">S</span>
          </div>
          <DialogTitle className="text-lg font-bold">Pay with Swish</DialogTitle>
          <DialogDescription className="text-base font-semibold text-foreground">
            {formattedAmount}
          </DialogDescription>
        </DialogHeader>

        {/* ── States ── */}

        {phase === 'creating' && (
          <div className="py-8 flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-[#00B67A]" />
            <p className="text-sm text-muted-foreground">Creating payment request…</p>
          </div>
        )}

        {phase === 'not_configured' && (
          <div className="py-6 flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <XCircle className="h-10 w-10 text-amber-500" />
            <p className="font-medium text-foreground">Swish not yet active</p>
            <p>
              Swish payments will be available once the business account is set up.
              Please pay with card instead.
            </p>
            <Button variant="outline" onClick={handleClose}>Close</Button>
          </div>
        )}

        {phase === 'waiting' && (
          <div className="flex flex-col items-center gap-4 py-2">
            {/* QR code — desktop */}
            {!isMobile && qrCodeUrl && (
              <div className="p-2 border-2 border-[#00B67A] rounded-xl overflow-hidden">
                <Image
                  src={qrCodeUrl}
                  alt="Swish QR code"
                  width={180}
                  height={180}
                 
                />
              </div>
            )}

            {/* Deep link button — mobile */}
            {isMobile && deepLink && (
              <Button
                className="w-full bg-[#00B67A] hover:bg-[#00a06d] text-white font-semibold"
                onClick={() => { window.location.href = deepLink; }}
              >
                <Smartphone className="h-4 w-4 mr-2" />
                Open Swish App
              </Button>
            )}

            {/* Instructions */}
            <p className="text-sm text-muted-foreground">
              {isMobile
                ? 'Tap the button to open your Swish app and confirm the payment.'
                : 'Scan the QR code with your Swish app to confirm the payment.'}
            </p>

            {/* Polling indicator */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Waiting for confirmation…
            </div>

            <Button variant="ghost" size="sm" onClick={handleClose} className="text-xs">
              Cancel
            </Button>
          </div>
        )}

        {phase === 'paid' && (
          <div className="py-8 flex flex-col items-center gap-3">
            <CheckCircle2 className="h-12 w-12 text-[#00B67A]" />
            <p className="font-semibold text-foreground">Payment confirmed!</p>
            <p className="text-sm text-muted-foreground">Your purchase is being processed…</p>
          </div>
        )}

        {phase === 'failed' && (
          <div className="py-6 flex flex-col items-center gap-3">
            <XCircle className="h-10 w-10 text-destructive" />
            <p className="font-semibold text-foreground">Payment failed</p>
            {errorMsg && (
              <p className="text-sm text-muted-foreground text-center">{errorMsg}</p>
            )}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={handleClose}>Close</Button>
              <Button onClick={startPayment}>Try Again</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
